import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../LanguageContext';
import './NoticePopup.css';

function NoticePopup() {
  const [popups, setPopups] = useState([]);
  const [closed, setClosed] = useState({}); // { [idx]: true } — 이번 세션에서 닫은 것

  useEffect(() => {
    fetch('/api/popups/active')
      .then(res => res.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        // "오늘 하루 보지 않기" 적용된 거 필터링
        const filtered = data.filter(p => {
          const k = `mediNotice_${p.IDX}_hideUntil`;
          const until = localStorage.getItem(k);
          if (!until) return true;
          return Date.now() >= parseInt(until, 10);
        });
        setPopups(filtered);
      })
      .catch(() => {});
  }, []);

  const closePopup = (idx, hideToday) => {
    if (hideToday) {
      const t = new Date();
      t.setHours(24, 0, 0, 0);
      localStorage.setItem(`mediNotice_${idx}_hideUntil`, t.getTime().toString());
    }
    setClosed(prev => ({ ...prev, [idx]: true }));
  };

  const visiblePopups = popups.filter(p => !closed[p.IDX]);
  if (visiblePopups.length === 0) return null;

  // 우선순위(PRIORITY) 높은 순으로 정렬 → 위치(POSITION)별 그룹으로 묶어
  // 같은 자리에 여러 개면 세로로 안 겹치게 쌓아 표시 (우선순위 높은 게 위)
  const sorted = [...visiblePopups].sort((a, b) => (b.PRIORITY || 0) - (a.PRIORITY || 0));
  const groups = {};
  sorted.forEach(p => {
    const pos = p.POSITION || 'top-left';
    (groups[pos] = groups[pos] || []).push(p);
  });

  return (
    <>
      {Object.entries(groups).map(([pos, list]) => {
        const handleOverlayClick = (e) => {
          // 어두운 배경(center/top-center) 클릭 시 그룹 전체 닫기
          if (e.target.classList.contains('medi-popup-overlay')) {
            list.forEach(p => closePopup(p.IDX, false));
          }
        };
        return (
          <div
            key={pos}
            className={`medi-popup-overlay pos-${pos}`}
            onClick={handleOverlayClick}
          >
            {/* 여러 팝업을 옆으로 나란히(가로) 배치 — 안 겹치고 크기도 안 줄임 */}
            <div className="medi-popup-stack">
              {list.map(p => (
                <PopupItem key={p.IDX} popup={p} onClose={closePopup} />
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function PopupItem({ popup, onClose }) {
  const [hideToday, setHideToday] = useState(false);
  const [drag, setDrag] = useState({ x: 0, y: 0 }); // 방문자가 끌어 옮긴 상대 위치(저장 안 함)
  const dragRef = useRef(null);
  const { lang } = useLang();
  const title = (lang === 'en' && popup.TITLE_EN) ? popup.TITLE_EN : popup.TITLE;
  const content = (lang === 'en' && popup.CONTENT_EN) ? popup.CONTENT_EN : popup.CONTENT;

  const handleClose = () => onClose(popup.IDX, hideToday);

  const handleContentClick = () => {
    if (popup.LINK_NOTICE_IDX) {
      // 공지사항 상세로 이동
      window.location.href = `/notice?id=${popup.LINK_NOTICE_IDX}`;
    } else if (popup.LINK_URL) {
      window.open(popup.LINK_URL, '_blank', 'noopener,noreferrer');
    }
  };

  // 드래그: 상단(로고바/제목헤더)을 잡고 끌면 이동. 마우스/터치 모두 지원.
  const onDragStart = (e) => {
    const t = e.touches ? e.touches[0] : e;
    dragRef.current = { startX: t.clientX, startY: t.clientY, orig: { ...drag } };

    const onMove = (ev) => {
      const m = ev.touches ? ev.touches[0] : ev;
      const d = dragRef.current;
      if (!d) return;
      setDrag({ x: d.orig.x + (m.clientX - d.startX), y: d.orig.y + (m.clientY - d.startY) });
      if (ev.cancelable) ev.preventDefault(); // 터치 스크롤 방지
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  // 각 팝업은 자기 폭 유지(안 줄임). 드래그 전엔 transform 미지정 → 등장 애니메이션(mpIn) 그대로 재생
  const boxStyle = { width: (popup.WIDTH || 400) + 'px', maxWidth: '90vw' };
  if (drag.x || drag.y) boxStyle.transform = `translate(${drag.x}px, ${drag.y}px)`;

  const isHtml = (content || '').includes('<');
  const imageOnly = popup.IMAGE_ONLY === 1 && popup.IMAGE_URL;
  const hasLink = popup.LINK_NOTICE_IDX || popup.LINK_URL;
  const clickStyle = hasLink ? { cursor: 'pointer' } : {};
  const onClickContent = hasLink ? handleContentClick : undefined;

  const showLogo = popup.SHOW_LOGO !== 0;
  const dragProps = { onMouseDown: onDragStart, onTouchStart: onDragStart };

  return (
    <div className="medi-popup-box" style={boxStyle} role="dialog" aria-labelledby={`pop-${popup.IDX}`}>
      {showLogo && (
        <div className="medi-popup-brand medi-popup-drag" {...dragProps}>
          <img src="/medilogis_logo.png" alt="MEDILOGIS" />
          <span className="medi-popup-brand-text">MEDI<b>LOGIS</b></span>
        </div>
      )}
      {imageOnly ? (
        <div
          className="medi-popup-image medi-popup-image-only"
          style={clickStyle}
          onClick={onClickContent}
        >
          <img src={popup.IMAGE_URL} alt={title || ''} />
        </div>
      ) : (
        <>
          <div className="medi-popup-header medi-popup-drag" {...dragProps}>
            <h3 className="medi-popup-title" id={`pop-${popup.IDX}`}>{title}</h3>
          </div>
          {popup.IMAGE_URL && (
            <div
              className="medi-popup-image"
              style={clickStyle}
              onClick={onClickContent}
            >
              <img src={popup.IMAGE_URL} alt={title} />
            </div>
          )}
          <div
            className="medi-popup-body"
            style={clickStyle}
            onClick={onClickContent}
          >
            {isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            ) : (
              <p style={{whiteSpace:'pre-wrap'}}>{content}</p>
            )}
          </div>
        </>
      )}
      <div className="medi-popup-footer">
        <label className="medi-popup-hide">
          <input
            type="checkbox"
            checked={hideToday}
            onChange={e => setHideToday(e.target.checked)}
          /> {lang === 'en' ? "Don't show today" : '오늘 하루 보지 않기'}
        </label>
        <button className="medi-popup-close" onClick={handleClose}>{lang === 'en' ? 'Close' : '닫기'}</button>
      </div>
    </div>
  );
}

export default NoticePopup;
